import JSZip from 'jszip';
import { parseBackendUtcToDate } from '@/lib/backendDate';
import { choiceLabel } from '@/lib/questionChoices';

const COMPTE_RENDU_FILENAME = 'Compte_rendu_session.txt';

/** Prénom / segment sûr pour nom de fichier (Windows + URL). */
export function slugifySessionName(name) {
  const raw = String(name || 'eleve')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return raw.slice(0, 48) || 'eleve';
}

/** Date-heure d'enregistrement de la session (champ `created_at`), format nom de fichier, aligné fr-FR. */
function formatSessionCreatedAtForFilename(createdAt) {
  const raw = createdAt != null && String(createdAt).trim() !== '' ? String(createdAt) : '';
  const d = raw ? parseBackendUtcToDate(raw) : null;
  if (!d || Number.isNaN(d.getTime())) return 'date_inconnue';

  const parts = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);

  const pick = (type) => parts.find((p) => p.type === type)?.value ?? '';
  const day = pick('day');
  const month = pick('month');
  const year = pick('year');
  const hour = pick('hour').replace(/\D/g, '') || '00';
  const minute = pick('minute').replace(/\D/g, '') || '00';

  return `${day}-${month}-${year}_${hour}-${minute}`;
}

/** Base du fichier zip : Prenom_12-05-2026_20-08 (sans extension), d'après `created_at` de la session. */
export function buildSessionZipBasename(childName, createdAt) {
  const slug = slugifySessionName(childName);
  const stamp = formatSessionCreatedAtForFilename(createdAt);
  return `${slug}_${stamp}`;
}

/** Libellés QCM : snapshot enregistré avec la réponse, sinon définition actuelle de la question (admin). */
function labelsForMcResponse(r, questions) {
  const raw = r?.choices_offered;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((x) => String(x).trim()).filter(Boolean);
  }
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p) && p.length > 0) {
        return p.map((x) => String(x).trim()).filter(Boolean);
      }
    } catch {
      /* ignore */
    }
  }
  const qid = r?.question_id != null ? String(r.question_id) : '';
  const q = Array.isArray(questions)
    ? questions.find((x) => x && String(x.id) === qid)
    : null;
  if (q && q.type === 'multiple_choice' && Array.isArray(q.choices)) {
    const ls = q.choices.map((c) => choiceLabel(c).trim()).filter(Boolean);
    return ls.length ? ls : null;
  }
  return null;
}

/** Libellés QCM depuis la définition actuelle d'une question (parcours admin). */
function mcLabelsFromQuestion(q) {
  if (!q || q.type !== 'multiple_choice' || !Array.isArray(q.choices)) return null;
  const ls = q.choices.map((c) => choiceLabel(c).trim()).filter(Boolean);
  return ls.length ? ls : null;
}

function sortQuestionsByOrder(qs) {
  return [...(qs || [])].sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
}

/**
 * Une entrée par question du parcours actuel (ordre admin), puis réponses orphelines.
 * Évite un rapport tronqué lorsque le professeur modifie le parcours après les sessions.
 */
function buildOrderedQuestionRowSpecs(session, questionsInput) {
  const questions = sortQuestionsByOrder(questionsInput);
  const responses = Array.isArray(session.responses) ? session.responses : [];
  const byId = new Map();
  for (const r of responses) {
    const id = r?.question_id != null ? String(r.question_id) : '';
    if (id) byId.set(id, r);
  }
  const usedIds = new Set();
  const entries = [];

  for (const q of questions) {
    const qid = q?.id != null ? String(q.id) : '';
    const r = qid ? byId.get(qid) : undefined;
    if (q?.type === 'identity') {
      entries.push({ kind: 'identity', q, r });
      if (r) usedIds.add(qid);
    } else if (r) {
      entries.push({ kind: 'response', q, r });
      usedIds.add(qid);
    } else {
      entries.push({ kind: 'gap', q });
    }
  }

  for (const r of responses) {
    const id = r?.question_id != null ? String(r.question_id) : '';
    if (!id || usedIds.has(id)) continue;
    entries.push({ kind: 'orphan', r });
  }

  return entries.map((entry, index) => {
    const numero = index + 1;
    if (entry.kind === 'identity') {
      const { q, r } = entry;
      const fromSession = session?.child_name ? String(session.child_name).trim() : '';
      const fromR =
        r?.text_response != null && String(r.text_response).trim() !== ''
          ? String(r.text_response).trim()
          : '';
      const texte = fromR || fromSession || null;
      return {
        numero,
        questionText: String(q?.text || '').trim(),
        choice: null,
        choiceOfferedLabels: null,
        texte,
        audioUrl: r?.audio_url || null,
        isGap: false,
        isOrphan: false,
      };
    }
    if (entry.kind === 'gap') {
      const q = entry.q;
      return {
        numero,
        questionText: String(q?.text || '').trim(),
        choice: null,
        choiceOfferedLabels: mcLabelsFromQuestion(q),
        texte: null,
        audioUrl: null,
        isGap: true,
        isOrphan: false,
      };
    }
    if (entry.kind === 'orphan') {
      const r = entry.r;
      const choice =
        r.choice_selected != null && String(r.choice_selected).trim() !== ''
          ? String(r.choice_selected)
          : null;
      return {
        numero,
        questionText:
          (r.question_text && String(r.question_text).trim())
          || '(question retirée du parcours ou non retrouvée dans le parcours actuel)',
        choice,
        choiceOfferedLabels: labelsForMcResponse(r, questionsInput),
        texte: r.text_response != null && String(r.text_response).trim() !== '' ? String(r.text_response) : null,
        audioUrl: r.audio_url || null,
        isGap: false,
        isOrphan: true,
      };
    }
    const { q, r } = entry;
    const choice =
      r.choice_selected != null && String(r.choice_selected).trim() !== ''
        ? String(r.choice_selected)
        : null;
    const questionText =
      (r.question_text && String(r.question_text).trim()) || String(q?.text || '').trim();
    return {
      numero,
      questionText,
      choice,
      choiceOfferedLabels: labelsForMcResponse(r, questionsInput),
      texte: r.text_response != null && String(r.text_response).trim() !== '' ? String(r.text_response) : null,
      audioUrl: r.audio_url || null,
      isGap: false,
      isOrphan: false,
    };
  });
}

/** Comportement historique : une ligne par entrée `responses`, dans l’ordre enregistré (parcours inconnu ou vide). */
function buildLegacyResponseRowSpecs(session, questionsInput) {
  const responses = Array.isArray(session.responses) ? session.responses : [];
  return responses.map((r, i) => {
    const numero = i + 1;
    const choice =
      r.choice_selected != null && String(r.choice_selected).trim() !== ''
        ? String(r.choice_selected)
        : null;
    return {
      numero,
      questionText: (r.question_text && String(r.question_text).trim()) || '',
      choice,
      choiceOfferedLabels: labelsForMcResponse(r, questionsInput),
      texte: r.text_response != null && String(r.text_response).trim() !== '' ? String(r.text_response) : null,
      audioUrl: r.audio_url || null,
      isGap: false,
      isOrphan: false,
    };
  });
}

function formatSessionDateReadable(createdAt) {
  const raw = createdAt != null && String(createdAt).trim() !== '' ? String(createdAt) : '';
  const d = raw ? parseBackendUtcToDate(raw) : null;
  if (!d || Number.isNaN(d.getTime())) return 'date inconnue';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/**
 * Texte du compte-rendu : données de session + alignement sur le parcours actuel lorsque fourni.
 */
function buildCompteRenduText(session, blocks) {
  const lines = [];
  const br = '\r\n';
  const modeParcours = blocks.reportMode !== 'reponses_seules';

  lines.push('COMPTE-RENDU DE LA SESSION');
  lines.push('');
  lines.push(`Prénom de l'enfant : ${session.child_name || '—'}`);
  lines.push(`Date et heure d'enregistrement de la session : ${formatSessionDateReadable(session.created_at)}`);
  lines.push(`Statut : ${session.completed ? 'Terminé' : 'En cours'}`);
  lines.push('');
  if (modeParcours) {
    lines.push(
      'Les blocs ci-dessous suivent l’ordre actuel du parcours (réglages professeur). '
        + 'Pour chaque question, l’énoncé affiché est celui enregistré au moment de la réponse lorsqu’il existe ; '
        + 'sinon le libellé actuel de la question. '
        + 'Une ligne « sans réponse » indique qu’aucune réponse n’est stockée pour cette question dans la session '
        + '(nouvelle question, saut, ou parcours différent au moment du passage). '
        + 'Les derniers blocs éventuels regroupent des réponses orphelines (question retirée ou identifiant modifié).'
    );
  } else {
    lines.push(
      'Les blocs ci-dessous reprennent uniquement les réponses enregistrées avec la session, dans l’ordre d’enregistrement. '
        + 'Le parcours actuel n’a pas été transmis pour ce téléchargement : les questions ajoutées après coup '
        + 'n’apparaissent pas ici.'
    );
  }
  lines.push('');
  lines.push('—'.repeat(50));

  if (blocks.prenomAudioFile) {
    lines.push('');
    lines.push('PRÉNOM (ENREGISTREMENT VOCAL)');
    lines.push(`Ouvrez le fichier audio suivant dans ce dossier : ${blocks.prenomAudioFile}`);
  } else if (blocks.prenomAudioNote) {
    lines.push('');
    lines.push('PRÉNOM (ENREGISTREMENT VOCAL)');
    lines.push(blocks.prenomAudioNote);
  }

  for (const row of blocks.questions) {
    lines.push('');
    lines.push('—'.repeat(50));
    lines.push(`Question ${row.numero}`);
    lines.push('');
    lines.push(
      modeParcours
        ? 'Énoncé de la question (au moment de la réponse, ou libellé actuel si aucune réponse en base) :'
        : 'Énoncé de la question (au moment de la réponse) :'
    );
    lines.push(row.questionText || '(texte de la question non conservé dans les données)');
    lines.push('');
    lines.push('Réponse de l’enfant :');
    const parts = [];
    const selected = row.choice != null && String(row.choice).trim() !== '' ? String(row.choice).trim() : '';
    if (row.choiceOfferedLabels?.length) {
      parts.push('  • Propositions affichées :');
      for (const label of row.choiceOfferedLabels) {
        const t = String(label).trim();
        const mark = selected && t === selected ? '  ← choix de l’enfant' : '';
        parts.push(`      — ${t}${mark}`);
      }
      if (selected && !row.choiceOfferedLabels.some((l) => String(l).trim() === selected)) {
        parts.push(`  • Choix enregistré (hors liste ci-dessus) : ${selected}`);
      }
    } else if (selected) {
      parts.push(`  • Choix sélectionné : ${selected}`);
    }
    if (row.texte) {
      parts.push(
        selected
          ? `  • Note (compte rendu) : ${row.texte}`
          : `  • Réponse écrite : ${row.texte}`
      );
    }
    if (row.audioFichier) {
      parts.push(`  • Fichier audio à ouvrir dans ce dossier (double-clic ou lecture avec le lecteur du PC) : ${row.audioFichier}`);
    } else if (row.audioNote) {
      parts.push(`  • ${row.audioNote}`);
    }
    if (row.isOrphan) {
      parts.push('  • Bloc archivé : réponse enregistrée pour une question absente du parcours actuel (énoncé et réponses tels qu’enregistrés).');
    }
    if (parts.length === 0) {
      if (row.isGap) {
        parts.push(
          '  • Aucune réponse enregistrée pour cette question dans la session (nouvelle question, saut, ou parcours différent au moment du passage).'
        );
      } else {
        parts.push('  • (aucune réponse enregistrée pour cette question)');
      }
    }
    lines.push(...parts);
  }

  lines.push('');
  lines.push('—'.repeat(50));
  lines.push('');
  lines.push(`Nombre de blocs (questions / archivage) dans ce rapport : ${blocks.questions.length}`);
  lines.push('');
  lines.push(
    'À propos des fichiers audio : ils portent l’extension du format enregistré par le navigateur '
      + '(ici : WebM / Opus). Ils s’ouvrent avec VLC, Chrome, Edge, etc.'
  );
  lines.push('');
  lines.push('Fin du compte-rendu.');

  return lines.join(br);
}

/** Extension fichier audio à partir du data URL et/ou du type du Blob (MediaRecorder = souvent webm). */
function audioFileExtensionFromSource(dataUrl, blob) {
  const fromMime = (mime) => {
    if (!mime || typeof mime !== 'string') return null;
    const m = mime.split(';')[0].trim().toLowerCase();
    if (m === 'audio/webm') return 'webm';
    if (m === 'audio/ogg' || m === 'audio/oga') return 'ogg';
    if (m === 'audio/mp4' || m === 'audio/m4a' || m === 'audio/aac') return 'm4a';
    if (m === 'audio/mpeg' || m === 'audio/mp3') return 'mp3';
    if (m === 'audio/wav' || m === 'audio/x-wav') return 'wav';
    if (m.startsWith('audio/')) return m.replace(/^audio\//, '').replace(/[^a-z0-9]/gi, '') || 'audio';
    return null;
  };

  if (typeof dataUrl === 'string' && dataUrl.startsWith('data:')) {
    const match = /^data:([^;,]+)/i.exec(dataUrl);
    const ext = fromMime(match?.[1]);
    if (ext) return ext;
  }
  const blobExt = fromMime(blob?.type);
  if (blobExt) return blobExt;
  return 'webm';
}

async function urlToBlob(url) {
  if (!url || typeof url !== 'string') return null;
  const res = await fetch(url);
  if (!res.ok) return null;
  return res.blob();
}

/**
 * ZIP : Compte_rendu_session.txt + fichiers audio (extension selon le type enregistré, en pratique WebM dans l’app).
 */
export async function downloadSessionZip(session, questions = []) {
  if (!session?.id) throw new Error('Session invalide');

  const zip = new JSZip();
  const basename = buildSessionZipBasename(session.child_name, session.created_at);

  let prenomAudioFile = null;
  let prenomAudioNote = null;

  if (session.child_name_audio_url) {
    try {
      const blob = await urlToBlob(session.child_name_audio_url);
      if (blob && blob.size > 0) {
        const ext = audioFileExtensionFromSource(session.child_name_audio_url, blob);
        prenomAudioFile = `prenom.${ext}`;
        zip.file(prenomAudioFile, blob);
      } else {
        prenomAudioNote =
          'Un enregistrement vocal du prénom était prévu, mais le fichier n’a pas pu être inclus dans ce dossier.';
      }
    } catch {
      prenomAudioNote =
        'Un enregistrement vocal du prénom était prévu, mais le fichier n’a pas pu être inclus dans ce dossier.';
    }
  }

  const useParcours = Array.isArray(questions) && questions.length > 0;
  const rowSpecs = useParcours
    ? buildOrderedQuestionRowSpecs(session, questions)
    : buildLegacyResponseRowSpecs(session, questions);

  const questionRows = [];
  for (const spec of rowSpecs) {
    const idx = String(spec.numero).padStart(3, '0');
    let audioFile = null;
    let audioNote = null;

    if (spec.audioUrl) {
      try {
        const blob = await urlToBlob(spec.audioUrl);
        if (blob && blob.size > 0) {
          const ext = audioFileExtensionFromSource(spec.audioUrl, blob);
          audioFile = `reponse_${idx}.${ext}`;
          zip.file(audioFile, blob);
        } else {
          audioNote =
            'Un fichier audio était associé à cette réponse, mais il n’a pas pu être copié dans ce dossier.';
        }
      } catch {
        audioNote =
          'Un fichier audio était associé à cette réponse, mais il n’a pas pu être copié dans ce dossier.';
      }
    }

    questionRows.push({
      numero: spec.numero,
      questionText: spec.questionText,
      choice: spec.choice,
      choiceOfferedLabels: spec.choiceOfferedLabels,
      texte: spec.texte,
      audioFichier: audioFile,
      audioNote,
      isGap: spec.isGap,
      isOrphan: spec.isOrphan,
    });
  }

  const compteRendu = buildCompteRenduText(session, {
    prenomAudioFile,
    prenomAudioNote,
    questions: questionRows,
    reportMode: useParcours ? 'parcours' : 'reponses_seules',
  });

  const bom = '\uFEFF';
  zip.file(COMPTE_RENDU_FILENAME, bom + compteRendu);

  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${basename}.zip`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
